import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';

import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';
import { ConnectionAuthKind, CredentialTypeOptions, servicePrincipalCredentialTypes, sharePointOnlineAuthTypes } from 'shell-models-lib';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { sharePointSpnRetirementDocLink } from 'app/shared/constants/links.const';

const servicePrincipalCredentialKeyControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            description: stringResources.SharePointOnlineList_ServicePrincipalKey_Description,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SharePointOnlineList_DisplayText),
        stringResources.ModelName_ServicePrincipalKey)
};

const servicePrincipalCertificateControls: ControlMetadata[] = [
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'servicePrincipalEmbeddedCert',
        label: stringResources.ModelName_ServicePrincipalCert,
        child: {
            type: 'parameterizable',
            attribute: 'servicePrincipalEmbeddedCert',
            disableAddParameter: true,
            child: {
                type: 'key-file-content',
                label: stringResources.ModelName_ServicePrincipalCert,
                required: true,
                isLabelVertical: true
            }
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SharePointOnlineList_DisplayText),
            stringResources.ModelName_ServicePrincipalCert)
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'servicePrincipalEmbeddedCertPassword',
        label: stringResources.ModelName_ServicePrincipalCertPassword,
        child: {
            type: 'parameterizable',
            attribute: 'servicePrincipalEmbeddedCertPassword',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_ServicePrincipalCertPassword,
                isLabelVertical: true
            }
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SharePointOnlineList_DisplayText),
            stringResources.ModelName_ServicePrincipalCertPassword)
    }
];

const dynamicServicePrincipalControl: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'servicePrincipalId',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_ServicePrincipalID,
            description: stringResources.SharePointOnlineList_ServicePrincipalId_Description,
            isLabelVertical: true
        }
    },
    // Show up for the existing resource with explicit servicePrincipalCredentialType defined as the ServicePrincipalKey
    {
        type: 'radio-switch',
        bind: 'servicePrincipalCredentialType',
        hide: '{ !isExistingServicePrincipalKeyResource }',
        label: stringResources.ModelName_ServicePrincipalCredentialType,
        isLabelVertical: true,
        nested: 1,
        options: [
            {
                label: format(stringResources.ConnectionWithWorkspaceNameTemplate, stringResources.ModelName_ServicePrincipalCert, stringResources.Recommended),
                value: servicePrincipalCredentialTypes.ServicePrincipalCert
            },
            {
                label: format(stringResources.ConnectionWithWorkspaceNameTemplate, stringResources.ModelName_ServicePrincipalKey, stringResources.DriverOption_Legacy),
                value: servicePrincipalCredentialTypes.ServicePrincipalKey
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        cases: [
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
                children: [servicePrincipalCredentialKeyControl]
            },
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
                children: servicePrincipalCertificateControls
            }
        ]
    },
    // Show up for the new resource, and the existing resource with explicit servicePrincipalCredentialType defined as the ServicePrincipalCert
    ...servicePrincipalCertificateControls.map((control) => ({
        ...control,
        hide: '{ isOldServicePrincipalResource || isExistingServicePrincipalKeyResource }'
    })),
    // Show up for the old resource without an explicit servicePrincipalCredentialType
    {
        ...servicePrincipalCredentialKeyControl,
        hide: '{ !isOldServicePrincipalResource }',
    }
];

const authenticationTypeControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: [
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: sharePointOnlineAuthTypes.ServicePrincipal
        },
        {
            label: stringResources.ModelName_UserAuthentication,
            value: sharePointOnlineAuthTypes.UserAuth
        }
    ],
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true,
    defaultValue: sharePointOnlineAuthTypes.ServicePrincipal,
    cases: [
        {
            caseValue: sharePointOnlineAuthTypes.ServicePrincipal,
            children: [...dynamicServicePrincipalControl]
        },
        {
            caseValue: sharePointOnlineAuthTypes.UserAuth,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAuth,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
            ]
        }
    ]
};

export const dynamicCommonControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'siteUrl',
            label: stringResources.ModelName_SiteUrl,
            description: stringResources.SharePointOnlineList_SiteURL_Description,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        warningMessageCalculator: (value: string) =>
            stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
    },
    {
        type: 'parameterizable',
        child: {
            type: 'tenant-textbox',
            bind: 'tenantId',
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            label: stringResources.ModelName_Tenant,
            description: stringResources.SharePointOnlineList_Tenant_Description,
            required: true
        }
    }
];

const dynamicSharePointControls: ControlMetadata[] = GlobalVariables.enableConnectorUserAuthCred
    ? dynamicCommonControls.concat([authenticationTypeControl])
    : dynamicCommonControls.concat(dynamicServicePrincipalControl);

export const sharePointOnlineListMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.sharePointOnlineList.linkedServiceType,
        displayName: stringResources.SharePointOnlineList_DisplayText,
        ...(!__IS_FABRIC_DPE__ ? {
            showWarningMessage: '{ isOldServicePrincipalResource || servicePrincipalCredentialType === "ServicePrincipalKey" }',
            warningMessage: format(
                stringResources.Connector_SharePointSpn_RetirementWarning,
                stringResources.SharePointOnlineList_DisplayText),
            warningMessageHyperlink: sharePointSpnRetirementDocLink
        } : {}),
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.SharepointOnlineList
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportedAuthKindsInTrident: [
            ConnectionAuthKind.ServicePrincipal, // kept for backward compatibility, we no longer supports new SPN creations (see UnifiedGetDataComponent)
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.WorkspaceIdentity
        ],
        supportCopyJobSource: true,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-sharepoint'
        },
        tableNameAlias: 'listName',
        isFirstClass: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.sharePointOnlineList.copySourceType],
        supportDatasetV2: true,
        category: ['ServicesApps'],
        onetimeQueryTemplate: '',
        // eslint-disable-next-line max-len
        timedQueryTemplate: '$filter=<<columnName>> ge datetime\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-ddTHH:mm:ssZ\' )}\' \nand <<columnName>> lt datetime\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-ddTHH:mm:ssZ\' )}\'',
        deltaLoadQueryTemplate: `$filter=<<columnName>> ge @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('datetime''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-ddTHH:mm:ssZ'), ''''))}
        and <<columnName>> lt @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('datetime''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-ddTHH:mm:ssZ'), ''''))}`,
        supportFirstClassParameterExperience: true,
        controls: dynamicSharePointControls
    };
    return metadata;
}
